import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faXmark,
  faCheck,
  faChevronDown,
  faSearch,
  faSpinner,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { useAxiosInstance } from "../../services/Interceptor";
import {
  fetchGraduationYears,
  fetchHighSchool,
  fetchPublicEvents,
} from "../../services/LeaderboardService";
import { positions } from "../../utils/PositionStateData";

const CONDITIONS = ["Select", "LessThan", "GreaterThan", "Equals"];

/* const SCORE_FILTER_TYPES = [
  { label: "PositionScore", value: 2 },
  { label: "TrackittMeasurementScore", value: 5 },
  { label: "PositionMeasurementScore", value: 6 },
]; */

const DropdownWithSearch = ({
  label,
  options,
  selectedValues = [],
  onSelect,
  isLoading = false,
  isObjectArray = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter((option) => {
    const displayValue = isObjectArray ? option?.label || "" : option || "";
    return displayValue
      .toString()
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
  });

  const handleOptionClick = (option) => {
    const newSelected = selectedValues.some((val) =>
      isObjectArray ? val?.value === option?.value : val === option,
    )
      ? selectedValues.filter((val) =>
          isObjectArray ? val?.value !== option?.value : val !== option,
        )
      : [...selectedValues, option];

    onSelect(newSelected);
    setIsOpen(true);
  };

  const formatSelectedLabel = () => {
    if (!selectedValues || selectedValues.length === 0) return "Select...";
    const [first, second, ...rest] = selectedValues;
    let text = isObjectArray ? first?.label || "" : first || "";
    if (second)
      text += `, ${isObjectArray ? second?.label || "" : second || ""}`;
    if (rest.length) text += ` & +${rest.length}`;
    return text;
  };

  const isSelected = (option) => {
    return selectedValues.some((val) =>
      isObjectArray ? val?.value === option?.value : val === option,
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} className="relative w-full md:w-64">
      <label className="block mb-1 text-sm font-medium text-gray-700">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center border px-4 py-2 rounded-md shadow-sm bg-white"
        disabled={isLoading}
      >
        <span className="truncate text-left">{formatSelectedLabel()}</span>
        {isLoading ? (
          <FontAwesomeIcon icon={faSpinner} className="ml-2 animate-spin" />
        ) : (
          <FontAwesomeIcon icon={faChevronDown} className="ml-2" />
        )}
      </button>

      {isOpen && (
        <div className="absolute mt-2 w-full bg-white border rounded-md shadow-lg z-30">
          <div className="p-2 border-b">
            <div className="flex items-center border rounded px-2">
              <FontAwesomeIcon icon={faSearch} className="text-gray-400 mr-2" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-1 text-sm outline-none"
              />
            </div>
          </div>
          <ul className="max-h-60 overflow-y-auto">
            {isLoading ? (
              <li className="px-4 py-2 text-center">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
              </li>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <li
                  key={isObjectArray ? option?.value : option}
                  onClick={() => handleOptionClick(option)}
                  className="flex justify-between items-center px-4 py-2 cursor-pointer hover:bg-gray-100"
                >
                  <span>
                    {isObjectArray ? option?.label || "" : option || ""}
                  </span>
                  {isSelected(option) && (
                    <FontAwesomeIcon
                      icon={faCheck}
                      className="text-blue-600 w-4 h-4"
                    />
                  )}
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-gray-500">No options found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const ConditionDropdown = ({
  label,
  operator,
  value,
  showDropdown,
  onOperatorChange,
  onValueChange,
  onToggleDropdown,
  placeholder = "0",
  hasError = false,
}) => {
  const [localValue, setLocalValue] = useState(value ?? "");

  useEffect(() => {
    setLocalValue(value ?? "");
  }, [value]);

  return (
    <div className="relative w-full md:w-64">
      <label className="block mb-1 font-medium text-gray-700">{label}</label>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative w-full sm:w-1/2">
          <button
            type="button"
            onClick={onToggleDropdown}
            className={`w-full flex justify-between items-center border rounded px-4 py-2 text-gray-700 bg-white ${
              hasError ? "border-red-500" : ""
            }`}
          >
            <span>{operator || "Select"}</span>
            <FontAwesomeIcon icon={faChevronDown} />
          </button>
          {showDropdown && (
            <ul className="absolute w-full mt-1 bg-white border rounded shadow z-10">
              {CONDITIONS.map((cond) => (
                <li
                  key={cond}
                  onClick={() => onOperatorChange(cond)}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                >
                  {cond}
                </li>
              ))}
            </ul>
          )}
        </div>
        <input
          type="number"
          placeholder={placeholder}
          value={localValue}
          onChange={(e) => {
            const v = e.target.value;
            setLocalValue(v);
            onValueChange(v);
          }}
          className={`border rounded px-4 py-2 text-gray-700 w-full sm:w-1/2 ${
            hasError ? "border-red-500" : ""
          }`}
          min="0"
          step="1"
        />
      </div>
    </div>
  );
};

const FilterPanel = ({ onApplyFilters, filterParams, onClearFilters }) => {
  const apiInstance = useAxiosInstance();
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState({
    years: false,
    events: false,
    schools: false,
  });
  const [validationErrors, setValidationErrors] = useState({});

  const LONG = "Interior Offensive Line (Guard or Center)";
  const DISPLAY = "Interior Offensive Line";

  const toDisplay = (p) => (p === LONG ? p : p);
  const toApi = (p) => (p === DISPLAY ? p : p);

  const [isPositionScoreOpen, setIsPositionScoreOpen] = useState(false);

  const positionScoreOptions = [
    { value: "PositionScore", label: "Overall Position Score" },
    { value: "PositionAthleticScore", label: "Position Athletic Score" },
    { value: "PositionMeasurementScore", label: "Position Measurement Score" },
  ];

  const positionsDisplay = positions.filter((p) => p !== "None");

  const [dropdownOptions, setDropdownOptions] = useState({
    year: [],
    position: positionsDisplay,
    eventIds: [],
    highSchools: [],
    states: [
      "Alabama (AL)",
      "Alaska (AK)",
      "Arizona (AZ)",
      "Arkansas (AR)",
      "California (CA)",
      "Colorado (CO)",
      "Connecticut (CT)",
      "Delaware (DE)",
      "Florida (FL)",
      "Georgia (GA)",
      "Hawaii (HI)",
      "Idaho (ID)",
      "Illinois (IL)",
      "Indiana (IN)",
      "Iowa (IA)",
      "Kansas (KS)",
      "Kentucky (KY)",
      "Louisiana (LA)",
      "Maine (ME)",
      "Maryland (MD)",
      "Massachusetts (MA)",
      "Michigan (MI)",
      "Minnesota (MN)",
      "Mississippi (MS)",
      "Missouri (MO)",
      "Montana (MT)",
      "Nebraska (NE)",
      "Nevada (NV)",
      "New Hampshire (NH)",
      "New Jersey (NJ)",
      "New Mexico (NM)",
      "New York (NY)",
      "North Carolina (NC)",
      "North Dakota (ND)",
      "Ohio (OH)",
      "Oklahoma (OK)",
      "Oregon (OR)",
      "Pennsylvania (PA)",
      "Rhode Island (RI)",
      "South Carolina (SC)",
      "South Dakota (SD)",
      "Tennessee (TN)",
      "Texas (TX)",
      "Utah (UT)",
      "Vermont (VT)",
      "Virginia (VA)",
      "Washington (WA)",
      "West Virginia (WV)",
      "Wisconsin (WI)",
      "Wyoming (WY)",
    ],
  });

  const [filters, setFilters] = useState({
    ...filterParams,
    showHeightDropdown: false,
    showWeightDropdown: false,
    showWingspanDropdown: false,
    showHandDropdown: false,
    showScoreConditionDropdown: false,
    scoreFilterTypes: filterParams.scoreFilterTypes || [],
  });

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        setLoading((prev) => ({ ...prev, years: true }));
        const years = await fetchGraduationYears(apiInstance);
        setDropdownOptions((prev) => ({ ...prev, year: years }));

        setLoading((prev) => ({ ...prev, events: true }));
        const events = await fetchPublicEvents(apiInstance);
        const formattedEvents = events.map((event) => ({
          label: event.name,
          value: event.id,
        }));
        setDropdownOptions((prev) => ({ ...prev, eventIds: formattedEvents }));

        setLoading((prev) => ({ ...prev, schools: true }));
        const schools = await fetchHighSchool(apiInstance);
        setDropdownOptions((prev) => ({ ...prev, highSchools: schools }));
      } catch (error) {
        console.error("Error fetching dropdown options:", error);
      } finally {
        setLoading({ years: false, events: false, schools: false });
      }
    };

    fetchDropdownData();
  }, [apiInstance]);

  useEffect(() => {
    if (dropdownOptions.eventIds.length > 0) {
      const updatedFilters = {
        ...filterParams,
        eventIds: filterParams.eventIds.map((id) => {
          if (typeof id === "object") {
            return id;
          }
          const event = dropdownOptions.eventIds.find((e) => e.value === id);
          return event || { value: id, label: `Event ${id}` };
        }),
        position: (filterParams.position || []).map(toDisplay),
        scoreFilterTypes: filterParams.scoreFilterTypes || [],
      };

      setFilters((prev) => ({ ...prev, ...updatedFilters }));

      if (
        filterParams.scoreFilterTypes &&
        filterParams.scoreFilterTypes.length > 0
      ) {
        //setShowScoreFilters(true);
      }
    }
  }, [filterParams, dropdownOptions.eventIds]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    /*  if (name === "scoreFilterTypes" && validationErrors.score) {
      setValidationErrors((prev) => ({ ...prev, score: null }));
    } */

    if (
      (name === "positionScoreType" || name === "positionScoreValue") &&
      validationErrors.positionScore
    ) {
      setValidationErrors((prev) => ({ ...prev, positionScore: null }));
    }
  };

  const validateFilters = () => {
    const errors = {};

    if (filters.scoreFilterTypes && filters.scoreFilterTypes.length > 0) {
      if (
        !filters.scoreFilterOperator ||
        filters.scoreFilterOperator === "Select"
      ) {
        errors.score = "Please select a condition for score filter";
      }

      const v = parseFloat(filters.positionScoreValue);
      if (isNaN(v) || v <= 0) {
        errors.score = "Please enter a valid score ≥ 1";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      year: [],
      position: [],
      eventIds: [],
      highSchools: [],
      states: [],
      heightFilterOperator: null,
      heightValue: "",
      weightFilterOperator: null,
      weightValue: "",
      wingspanFilterOperator: null,
      wingspanValue: "",
      handFilterOperator: null,
      handValue: "",
      scoreFilterTypes: [],
      scoreFilterOperator: null,
      positionScoreValue: "",
      showHeightDropdown: false,
      showWeightDropdown: false,
      showWingspanDropdown: false,
      showHandDropdown: false,
      showScoreConditionDropdown: false,
    };

    setFilters(clearedFilters);
    setValidationErrors({});
    setShowFilters(true);
  };

  const handleApplyFilters = () => {
    if (!validateFilters()) {
      onClearFilters();
      return;
    }

    const payload = {
      ...filters,
      positionScoreValue:
        filters.positionScoreValue === "" || filters.positionScoreValue === null
          ? ""
          : Number(filters.positionScoreValue),
      position: (filters.position || []).map(toApi),
      eventIds: filters.eventIds,
    };

    onApplyFilters(payload);
    setShowFilters(false);
  };

  return (
    <div className="p-0">
      <button
        className="bg-gray-200 mb-2 border border-gray-300 rounded-md px-4 py-2 flex items-center gap-2"
        onClick={() => setShowFilters(!showFilters)}
      >
        <FontAwesomeIcon icon={showFilters ? faXmark : faFilter} />
        {showFilters ? "Close Filters" : "Filters"}
      </button>

      {showFilters && (
        <div className="border border-gray-300 absolute left-0 xl:left-24 mx-6 sm:mx-auto max-w-full bg-white rounded-lg shadow space-y-4 z-20">
          <h2 className="text-lg font-bold pl-4 pt-4">Filters</h2>

          <div className="flex flex-col md:flex-row md:flex-wrap gap-6 px-4">
            <DropdownWithSearch
              label="Year"
              options={dropdownOptions.year}
              selectedValues={filters.year}
              onSelect={(values) => handleFilterChange("year", values)}
              isLoading={loading.years}
            />
            <DropdownWithSearch
              label="Position"
              options={dropdownOptions.position}
              selectedValues={filters.position}
              onSelect={(values) => handleFilterChange("position", values)}
            />
            <DropdownWithSearch
              label="Events"
              options={dropdownOptions.eventIds}
              selectedValues={filters.eventIds}
              onSelect={(values) => handleFilterChange("eventIds", values)}
              isLoading={loading.events}
              isObjectArray={true}
            />
            <DropdownWithSearch
              label="School"
              options={dropdownOptions.highSchools}
              selectedValues={filters.highSchools}
              onSelect={(values) => handleFilterChange("highSchools", values)}
              isLoading={loading.schools}
            />
            <DropdownWithSearch
              label="State"
              options={dropdownOptions.states}
              selectedValues={filters.states}
              onSelect={(values) => handleFilterChange("states", values)}
            />
          </div>

          <div className="flex flex-col md:flex-row md:flex-wrap gap-6 px-4">
            <ConditionDropdown
              label="Height"
              operator={filters.heightFilterOperator}
              value={filters.heightValue}
              showDropdown={filters.showHeightDropdown}
              onOperatorChange={(operator) =>
                setFilters((prev) => ({
                  ...prev,
                  heightFilterOperator: operator,
                  showHeightDropdown: false,
                }))
              }
              onValueChange={(val) =>
                setFilters((prev) => ({
                  ...prev,
                  heightValue: val,
                }))
              }
              onToggleDropdown={() =>
                setFilters((prev) => ({
                  ...prev,
                  showHeightDropdown: !prev.showHeightDropdown,
                }))
              }
              placeholder="0"
            />

            <ConditionDropdown
              label="Weight"
              operator={filters.weightFilterOperator}
              value={filters.weightValue}
              showDropdown={filters.showWeightDropdown}
              onOperatorChange={(operator) =>
                setFilters((prev) => ({
                  ...prev,
                  weightFilterOperator: operator,
                  showWeightDropdown: false,
                }))
              }
              onValueChange={(val) =>
                setFilters((prev) => ({
                  ...prev,
                  weightValue: val,
                }))
              }
              onToggleDropdown={() =>
                setFilters((prev) => ({
                  ...prev,
                  showWeightDropdown: !prev.showWeightDropdown,
                }))
              }
              placeholder="0"
            />

            <ConditionDropdown
              label="Wingspan"
              operator={filters.wingspanFilterOperator}
              value={filters.wingspanValue}
              showDropdown={filters.showWingspanDropdown}
              onOperatorChange={(operator) =>
                setFilters((prev) => ({
                  ...prev,
                  wingspanFilterOperator: operator,
                  showWingspanDropdown: false,
                }))
              }
              onValueChange={(val) =>
                setFilters((prev) => ({
                  ...prev,
                  wingspanValue: val,
                }))
              }
              onToggleDropdown={() =>
                setFilters((prev) => ({
                  ...prev,
                  showWingspanDropdown: !prev.showWingspanDropdown,
                }))
              }
              placeholder="0"
            />

            <ConditionDropdown
              label="Hand"
              operator={filters.handFilterOperator}
              value={filters.handValue}
              showDropdown={filters.showHandDropdown}
              onOperatorChange={(operator) =>
                setFilters((prev) => ({
                  ...prev,
                  handFilterOperator: operator,
                  showHandDropdown: false,
                }))
              }
              onValueChange={(val) =>
                setFilters((prev) => ({
                  ...prev,
                  handValue: val,
                }))
              }
              onToggleDropdown={() =>
                setFilters((prev) => ({
                  ...prev,
                  showHandDropdown: !prev.showHandDropdown,
                }))
              }
              placeholder="0"
            />

            <div className="w-full md:w-64 relative">
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Position Scores
              </label>

              {/* Dropdown trigger */}
              <button
                type="button"
                onClick={() => setIsPositionScoreOpen((prev) => !prev)}
                className={`w-full flex justify-between items-center border rounded px-4 py-2 bg-white text-left ${
                  validationErrors.positionScore ? "border-red-500" : ""
                }`}
              >
                <span className="truncate">
                  {positionScoreOptions.find(
                    (opt) => opt.value === filters.positionScoreType,
                  )?.label || "Select score type"}
                </span>
                <FontAwesomeIcon icon={faChevronDown} />
              </button>

              {/* Dropdown list */}
              {isPositionScoreOpen && (
                <ul className="absolute z-20 mt-1 w-full bg-white border rounded shadow">
                  {positionScoreOptions.map((option) => (
                    <li
                      key={option.value}
                      onClick={() => {
                        handleFilterChange("positionScoreType", option.value);
                        setIsPositionScoreOpen(false);
                      }}
                      className="px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                    >
                      {option.label}
                    </li>
                  ))}
                </ul>
              )}

              {/* Score input */}
              <label className="block mt-2 mb-1 text-sm font-medium text-gray-700">
                Score (greater than or equal)
              </label>
              <input
                type="number"
                min="0"
                step="1"
                className={`w-full border rounded px-4 py-2 text-gray-700 ${
                  validationErrors.positionScore ? "border-red-500" : ""
                }`}
                value={filters.positionScoreValue}
                onChange={(e) =>
                  handleFilterChange("positionScoreValue", e.target.value)
                }
                placeholder="0"
              />

              {/* Error */}
              {validationErrors.positionScore && (
                <div className="flex items-center gap-2 text-red-600 text-sm mt-2">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="w-3 h-3"
                  />
                  <span>{validationErrors.positionScore}</span>
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-100 p-4 rounded-b-lg">
            <div className="flex justify-end gap-4">
              <button
                className="px-6 py-2 border rounded bg-white"
                onClick={handleClearFilters}
              >
                Clear All
              </button>
              <button
                className="px-6 py-2 bg-red-500 text-white rounded"
                onClick={handleApplyFilters}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;
